import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogIn, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { setToken } from "../../lib/adminApi";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) {
        // Map common statuses to friendly messages first (some ingress configs
        // strip the JSON body on non-2xx responses).
        let msg;
        if (r.status === 401) msg = "Invalid credentials";
        else if (r.status === 422) msg = "Please enter a valid email and password";
        else {
          const d = await r.json().catch(() => ({}));
          const detail = d.detail;
          msg =
            typeof detail === "string"
              ? detail
              : Array.isArray(detail)
              ? detail.map((x) => x.msg).join(" ")
              : `Login failed (${r.status})`;
        }
        throw new Error(msg);
      }
      const data = await r.json();
      setToken(data.access_token);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 p-4"
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border-4 border-purple-300 p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-pink-300 to-purple-400 rounded-2xl flex items-center justify-center shadow-lg mb-3">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-purple-700">Admin Dashboard</h1>
          <p className="text-sm text-purple-500 mt-1">Hola Spanglish</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          <div className="space-y-1.5">
            <Label htmlFor="admin-email" className="text-purple-700 font-bold text-sm">
              Email
            </Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@email.com"
              autoComplete="username"
              required
              className="border-2 border-purple-300 focus:border-pink-400 rounded-xl"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-password" className="text-purple-700 font-bold text-sm">
              Password
            </Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="border-2 border-purple-300 focus:border-pink-400 rounded-xl"
            />
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border-2 border-red-200 rounded-xl p-3"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 hover:from-pink-500 hover:to-blue-500 text-white font-bold py-5 text-base rounded-full border-3 border-purple-300 transition-all duration-300 hover:scale-[1.02]"
          >
            {loading ? "Signing in…" : (<><LogIn className="mr-2 h-4 w-4" />Sign in</>)}
          </Button>
        </form>

        <p className="text-xs text-purple-400 text-center mt-6">
          Only the site owner can access this page.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
